import React, { useEffect, useRef, useState } from "react";
import HoverVideoPlayer from "react-hover-video-player";
import { resolveFeedCardImage } from "../../../Utils/media/feedCardImage";
import {
  CatalogEagerMediaContext,
  FadeInImage,
} from "../../../Screens/DashboardView/MasterTemplateEditor/TemplateRevealImage";
import { useFeedMediaPriority } from "../../../Utils/media/feedMediaPriority";
import {
  BsFillPlayFill,
  BsHourglassSplit,
  BsVolumeMute,
  BsVolumeUp,
} from "react-icons/bs";
import { isInsideWebsiteTemplatePage } from "../../../Utils/websiteTemplateGuards";

export interface CreatorFeedVideoPlayerProps {
  media_url?: string;
  thumbnail?: string;
  /** When true, show mute/unmute on hover (template search + LiB surfaces that opt in). */
  isSoundOn?: boolean;
  /** Hide the built-in hover sound control (shop cards render their own). */
  hideHoverSound?: boolean;
  /** Hide the paused-state play triangle (e.g. Content → YouTube thumbnails). */
  hidePlayButton?: boolean;
  muted?: boolean;
  onMutedChange?: (muted: boolean) => void;
  /** Force playback (e.g. when hover is tracked on a parent wrap). */
  focused?: boolean;
  /**
   * Bind hover to a parent tile (e.g. media wrap that also holds an external
   * play/mute button) so moving onto that chrome does not pause / remute.
   */
  hoverTarget?: React.RefObject<HTMLElement | null>;
  className?: string;
  style?: React.CSSProperties;
  showClickablePlayBtn?: boolean;
  children?: React.ReactNode;
}

const OverlayStatus = ({
  hover,
  onPlay,
  hidden,
}: {
  hover: boolean;
  showClickablePlayBtn?: boolean;
  onPlay: (e: React.MouseEvent<SVGElement>) => void;
  hidden?: boolean;
}) => {
  if (hidden || hover) return null;
  return <BsFillPlayFill className="creator-feed-card-play-btn" onClick={onPlay} />;
};

/**
 * Shared creator-feed video tile (search results, Twitter, Instagram, etc.).
 * Optional hover sound control matches link-in-bio / StayUpToDate video tiles.
 */
export function CreatorFeedVideoPlayer({
  media_url,
  thumbnail,
  isSoundOn = false,
  hideHoverSound = false,
  hidePlayButton = false,
  muted: mutedProp,
  onMutedChange,
  focused = false,
  hoverTarget,
  className = "creator-feed-card-video hover-play",
  style,
  showClickablePlayBtn = false,
  children,
}: CreatorFeedVideoPlayerProps) {
  const priority = useFeedMediaPriority();
  const eagerCatalog = React.useContext(CatalogEagerMediaContext);
  const shellRef = useRef<HTMLDivElement | null>(null);
  /** Website template chrome (play/mute) — hover must include these controls. */
  const chromeRef = useRef<HTMLDivElement | null>(null);
  // Off-screen tiles keep a poster only. Mounting HoverVideoPlayer creates a
  // <video> (and a metadata request) even when preload is "none".
  const [nearViewport, setNearViewport] = useState(priority);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const focusedRef = useRef(focused);
  focusedRef.current = focused;
  const [hover, setHover] = useState(false);
  const [internalMuted, setInternalMuted] = useState(true);
  const isMuted = mutedProp ?? internalMuted;
  const [customPause, setCustomPause] = useState(false);
  const [manualPlaying, setManualPlaying] = useState(false);
  const manualPlayingRef = useRef(manualPlaying);
  manualPlayingRef.current = manualPlaying;
  const isPlaying = hover || focused || manualPlaying;
  const isTemplate = isInsideWebsiteTemplatePage();
  // Template search/home: zero overlay crossfade — loading↔paused poster fade
  // looked like tiles flickering after paint.
  const overlayMs = isTemplate ? 0 : 120;

  useEffect(() => {
    if (priority) {
      setNearViewport(true);
      return;
    }
    // Content/Search posters stay mounted while scrolling. Observing every
    // tile here would setState on each one a fast fling passes, and that
    // re-render is what blanks the page.
    if (eagerCatalog && !focused && !manualPlaying) return;
    const el = shellRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setNearViewport(Boolean(entry?.isIntersecting)),
      { rootMargin: "200px 0px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [priority, media_url, eagerCatalog, focused, manualPlaying]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playsInline = true;
    if (!isPlaying) {
      video.muted = true;
      video.pause();
      return;
    }
    video.muted = isMuted;
    if (!isMuted) video.volume = 1;
  }, [isMuted, isPlaying, media_url]);

  const applyMuted = (next: boolean) => {
    const video = videoRef.current;
    if (video) {
      video.muted = next || !isPlaying;
      if (!next && isPlaying) video.volume = 1;
    }
    if (onMutedChange) onMutedChange(next);
    else setInternalMuted(next);
  };

  const remuteAndStop = () => {
    setManualPlaying(false);
    if (mutedProp === undefined) setInternalMuted(true);
    else onMutedChange?.(true);
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.pause();
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    applyMuted(!isMuted);
  };

  const togglePlay = (e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setManualPlaying((playing) => !playing);
  };

  const liftChrome = isTemplate && !hidePlayButton && !hideHoverSound;
  const thumbSrc = thumbnail || "/assets/dummy_video.png";
  const hasRealThumbnail = Boolean(
    thumbnail &&
      String(thumbnail).trim() &&
      !String(thumbnail).includes("dummy_video"),
  );

  const posterSrc = resolveFeedCardImage(thumbSrc, priority || eagerCatalog).src;
  const pausedPoster = hasRealThumbnail ? (
    <FadeInImage
      src={posterSrc}
      alt=""
      className="creator-feed-card-video creator-feed-paused-overlay"
      loading={priority || eagerCatalog ? "eager" : "lazy"}
      decoding="async"
      {...(priority
        ? ({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)
        : {})}
    />
  ) : (
    <div
      className="creator-feed-card-video creator-feed-paused-overlay creator-feed-paused-fallback"
      aria-hidden
    />
  );

  if (!media_url) {
    return (
      <>
        <div className="creator-feed-card-video not-available">
          <BsHourglassSplit className="hourglassSplit" />
          <p>
            Work in progress: This content will be available in a few minutes.
          </p>
        </div>
        {children}
      </>
    );
  }

  const posterOnly = (
    <div className={className} style={{ display: "block", width: "100%", ...style }}>
      {pausedPoster}
      <OverlayStatus
        hover={false}
        showClickablePlayBtn={showClickablePlayBtn}
        onPlay={togglePlay}
        hidden={hidePlayButton}
      />
      {children}
    </div>
  );

  // Bind hover to chrome wrap so moving onto play/mute does not end hover.
  const resolvedHoverTarget =
    hoverTarget || (liftChrome ? chromeRef : undefined);

  const handleHoverStart = () => {
    setHover(true);
    setCustomPause(false);
  };

  const handleHoverEnd = () => {
    setHover(false);
    setCustomPause(false);
    // Website template: always stop + remute on leave so the sound control
    // does not stick after cursor leave (empty LiB posts feed / Home).
    if (isTemplate) {
      remuteAndStop();
      return;
    }
    // Non-template: keep click-to-play / parent-focused sessions alive.
    if (focusedRef.current || manualPlayingRef.current) return;
    remuteAndStop();
  };

  const player = (
    <HoverVideoPlayer
      videoRef={videoRef}
      videoSrc={media_url}
      // Container owns aspect-ratio (CSS on className). Overlay-sized mode
      // resized to the poster and jumped when video loaded/unloaded (flicker).
      sizingMode="container"
      style={
        liftChrome
          ? { display: "block", width: "100%", height: "100%" }
          : { display: "block", width: "100%", ...style }
      }
      className={liftChrome ? "mte-tpl-player-fill" : className}
      muted={isSoundOn ? isMuted || !isPlaying : true}
      focused={isPlaying}
      // Template grids/carousels can mount dozens of tiles — avoid creating a
      // decoder until hover. Non-template keeps metadata for snappier hover.
      preload={isTemplate ? "none" : "metadata"}
      contextMenu="return false;"
      overlayTransitionDuration={overlayMs}
      {...(resolvedHoverTarget ? { hoverTarget: resolvedHoverTarget } : {})}
      hoverOverlay={
        liftChrome ? null : isSoundOn && !hideHoverSound ? (
          <div
            className="sound-icon"
            style={{ pointerEvents: "auto" }}
            onClick={toggleMute}
          >
            {isMuted ? (
              <BsVolumeMute size={24} color="white" />
            ) : (
              <BsVolumeUp size={24} color="white" />
            )}
          </div>
        ) : (
          <div className="video-hover" />
        )
      }
      pausedOverlay={
        <div className="pause-overlay">
          {pausedPoster}
          <OverlayStatus
            hover={isPlaying}
            showClickablePlayBtn={showClickablePlayBtn}
            onPlay={togglePlay}
            hidden={hidePlayButton || liftChrome}
          />
        </div>
      }
      loadingOverlay={pausedPoster}
      // Always unload when paused. Keeping players loaded on template
      // (search + carousels) blows past Chrome's WebMediaPlayer cap and
      // spams: "Blocked attempt to create a WebMediaPlayer...".
      unloadVideoOnPaused
      onHoverStart={handleHoverStart}
      onHoverEnd={handleHoverEnd}
      disableDefaultEventHandling={customPause}
    />
  );

  // Content and Search already have the poster painted. Mounting
  // HoverVideoPlayer for every tile a fast scroll passes creates a <video>
  // each time, and Chrome blanks the page. Play only while hovered or playing.
  const mountPlayer = eagerCatalog
    ? focused || (manualPlaying && nearViewport)
    : nearViewport;

  const interactive = mountPlayer ? (
    liftChrome ? (
      <div
        ref={chromeRef}
        className={`${className} mte-tpl-player-chrome`}
        style={style}
      >
        {player}
        <div className={`mte-tpl-media-toggle${isPlaying ? " is-playing" : ""}`}>
          <button
            type="button"
            className="mte-tpl-media-toggle-play"
            aria-label={isPlaying ? "Pause video" : "Play video"}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (isPlaying) {
                setManualPlaying(false);
                setCustomPause(true);
                setHover(false);
                remuteAndStop();
              } else {
                setCustomPause(false);
                setManualPlaying(true);
              }
            }}
          >
            <BsFillPlayFill />
          </button>
          {isSoundOn && !hideHoverSound ? (
            <button
              type="button"
              className="mte-tpl-media-toggle-sound"
              aria-label={isMuted ? "Unmute" : "Mute"}
              onClick={toggleMute}
            >
              {isMuted ? (
                <BsVolumeMute size={14} color="white" />
              ) : (
                <BsVolumeUp size={14} color="white" />
              )}
            </button>
          ) : null}
        </div>
        {children}
      </div>
    ) : (
      <>
        {player}
        {children}
      </>
    )
  ) : (
    posterOnly
  );

  return (
    <div ref={shellRef} className="feed-video-observe">
      {interactive}
    </div>
  );
}

export default CreatorFeedVideoPlayer;
